import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { DocumentDao } from '@app/dao/document/document.dao';
import { ProductDao } from '@app/dao/product/product.dao';
import { API_ROUTE_DOCUMENT } from '@app/forms/subscription/api/api-router';
import { CompanyFieldType } from '@app/forms/subscription/definition/interface/enum';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import {
  forkJoin,
  Observable,
  of,
  Subject,
  throwError,
  catchError,
  delay,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import {
  VerifyIdentityDocumentService,
  VeryfyRespons,
} from '../../services/verifyIdentityDocument.service';
import { countPdfPagesInFile } from '@app/components/pr-ged-picker/ged-document-tags';
import { EaDocumentFormBuilder } from './ea-subscription-form.builder';

const documentRequirements = {
  CNI: { minFiles: 2, errorMsg: 'Veuillez ajouter un document CNI valide' },
  PASSEPORT: {
    minFiles: 1,
    errorMsg: 'Veuillez ajouter un document PASSEPORT valide',
  },
  // PERMIS_DE_CONDUIRE: {
  //   minFiles: 1,
  //   errorMsg: "Veuillez ajouter un document Permis de conduire valide",
  // },
  // TITRE_DE_SEJOUR: {
  //   minFiles: 2,
  //   errorMsg: "Veuillez ajouter un document Titre de séjour valide",
  // },
};
const labelMap = {
  CNI: "Carte d'identitée Recto - Verso",
  PASSEPORT: 'Passeport',
  // PERMIS_DE_CONDUIRE: "Permis de conduire",
  // TITRE_DE_SEJOUR: "Titre de séjour",
};

// Taille maximale d'une pièce d'identité — alignée sur le backend (4 Mo).
const MAX_IDENTITY_FILE_SIZE = 4 * 1024 * 1024;
@UntilDestroy()
@Component({
  selector: 'document-form',
  templateUrl: './document-form.component.html',
  styleUrls: ['./document-form.component.scss'],
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => DocumentFormComponent),
    },
  ],
  standalone: false,
})
export class DocumentFormComponent
  extends EaDocumentFormBuilder
  implements OnInit
{
  files: { ownerId: number; files: any[] }[] = [];

  file: any;

  error: string;

  user: any;
  documents: any;
  docLoading: boolean = false;
  docLoadingIndex: number;
  doneSubject: Subject<void> = new Subject<void>();
  obser$: any;
  formData: any;
  typeDocument = [
    { label: 'CNI', code: 'CNI', category: 'CIVIL_STATE', type: [] },
    {
      label: 'Passeport',
      code: 'PASSEPORT',
      category: 'CIVIL_STATE',
      type: [],
    },
    // {
    //   label: "Permis de conduire",
    //   code: "PERMIS_DE_CONDUIRE",
    //   category: "CIVIL_STATE",
    //   type: [],
    // },
    // {
    //   label: "Titre de séjour",
    //   code: "TITRE_DE_SEJOUR",
    //   category: "CIVIL_STATE",
    //   type: [],
    // },
  ];
  documentId: any;
  resp: any[] = [];
  pledge: PledgeRead;
  IDENTITY_DOCUMENT: boolean;
  documentTypes = [
    'CNI',
    'TITRE_DE_SEJOUR',
    'PASSEPORT',
    'JUSTIFICATIF_DE_DOMICILE',
    'JUSTIFICATIF_ORIGINE_DES_FONDS',
    'STATUS_SOCIETE',
    'KBIS_SOCIETE',
  ];

  verificationResults: {
    [key: number]: { display: string; icon: string; text: string };
  } = {};
  certif: boolean = false;
  isRequestPending: boolean = false;
  obserVeryfy$: Observable<any>;
  iframeUrls: SafeResourceUrl[] = [];
  displayDocumentLoading: boolean = false;

  gedDocuments = ['CNI', 'PASSEPORT'];

  constructor(
    protected formConstructor: PrFormConstructor,
    private documentDao: DocumentDao,
    protected subscriptionFacade: SubscriptionFacadeService,
    protected authenticationService: AuthentificationService,
    private cdr: ChangeDetectorRef,
    private httpApiService: HttpApiService,
    private productsDao: ProductDao,
    private verificationService: VerifyIdentityDocumentService,
    private sanitizer: DomSanitizer,
  ) {
    super(formConstructor, subscriptionFacade, authenticationService);
  }

  ngOnInit() {
    this.initializePledge();
    this.initializeIdentityDocument();

    this.initVerificationResults();

    this.initializeFormWithDocuments();
  }
  private initVerificationResults() {
    this.certif = this.verificationService.calculateCertif(this.pledge);
    if (this.certif) {
      const verificationObservables = this.pledge.owners.map(({ userId }) => {
        return this.verifyIdentityDocument(userId);
      });
      this.obserVeryfy$ = forkJoin(verificationObservables);
    } else {
      this.obserVeryfy$ = of(true);
    }
  }
  private handleVerificationResult(
    userId: number,
    result: boolean,
    message: string,
  ) {
    const successText = "La pièce d'identité est au bon format.";
    const dangerText =
      "La pièce d'identité n'est pas au bon format pour Universign, la signature électronique ne sera pas disponible.";
    return this.verificationService.handleVerificationResult({
      userId: userId,
      result,
      successText,
      dangerText: message || dangerText,
    });
  }
  private verifyIdentityDocument(userId: number) {
    return this.verificationService
      .verifyIdentityDocuments(userId, this.pledge?.accountId as number)
      .pipe(
        catchError(() => of(false)),
        map((result: VeryfyRespons) => {
          this.verificationResults[userId] = this.handleVerificationResult(
            userId,
            result?.valid,
            result?.message,
          );
          this.cdr.detectChanges();
          return !!result?.valid;
        }),
      );
  }

  private initializeFormWithDocuments() {
    this.initForm({}, { ...this.pledge?.parameters });

    this.obser$ = this.obserVeryfy$.pipe(
      switchMap(() => {
        return this.subscriptionFacade.initFormWithDocuments(this, [
          'CNI',
          'PASSEPORT',
          'PERMIS_DE_CONDUIRE',
          'TITRE_DE_SEJOUR',
        ]);
      }),
      tap(documents => {
        this.loading = true;

        this.initForm(
          { ...documents },
          {
            ...this.pledge?.parameters,
          },
        );
        return documents;
      }),
      tap((documents: any) => {
        this.initIframeUrls(documents);
      }),

      finalize(() => {
        this.cdr.detectChanges();
      }),
    );
  }
  private initIframeUrls(documents: any) {
    const documentIdsToCheck = [
      'gedCniId',
      'gedPassportId',
      'gedTitreDeSejourId',
      'gedPermisDeConduireId',
    ];
    documents?.owners.forEach((owner, index) => {
      const existingDocumentId = documentIdsToCheck.find(
        documentId => owner?.[documentId],
      );
      if (owner?.documents?.id || existingDocumentId) {
        const documentIdToDisplay =
          owner?.[existingDocumentId] || owner?.documents?.id;
        this.displayDocument(documentIdToDisplay, index).subscribe();
      }
    });
  }

  private initializePledge(): void {
    this.pledge = this.subscriptionFacade.getPledge();
  }

  private initializeIdentityDocument(): void {
    const identityQuestion = this.pledge?.parameters?.additionalQuestions?.find(
      q => q?.id === CompanyFieldType.IDENTITY_DOCUMENT,
    );

    this.IDENTITY_DOCUMENT = identityQuestion?.required === 'MANDATORY';
  }

  onLoadFiles(files: any, ownerId: number) {
    const filesObj = { ownerId, files };

    if (!_.find(this.files, filesObj)) {
      this.files.push(filesObj);
    }
    if (this.files.length > 0) {
      this.files.forEach(file => {
        if (file.files.length === 0) {
          this.files = _.without(this.files, file);
        }
      });
    }

    if (this.files.length > 0) {
      this.error = null;
    }
  }

  get title() {
    return 'Ajoutez un nouveau document';
  }
  downloadDocument(doc, index) {
    this.docLoading = true;
    this.docLoadingIndex = index;
    const route = API_ROUTE_DOCUMENT.replace(
      ':accountId',
      this.entity?.accountId,
    ).replace(':documentId', doc.id);

    this.httpApiService
      .getWithOptions(route, {
        responseType: 'arraybuffer',
      })
      .pipe(
        take(1),
        catchError(err => {
          this.docLoading = false;
          console.error(err);

          return err;
        }),
        finalize(() => {
          this.docLoading = false;
        }),
      )
      .subscribe(resp => {
        var blob = new Blob([resp], { type: 'application/pdf' });
        var link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${doc.label}.pdf`;
        link.click();

        this.docLoading = false;

        this.cdr.detectChanges();
      });
  }

  deleteDocument(doc, index, ownerId) {
    const route = API_ROUTE_DOCUMENT.replace(
      ':accountId',
      this.entity?.accountId,
    ).replace(':documentId', doc.id);
    this.httpApiService
      .delete(route)
      .pipe(take(1))
      .subscribe(() => {
        this.resetOwnerDocument(index);
        this.cdr.detectChanges();
      });
  }

  withdraw(index) {
    this.resetOwnerDocument(index);
    this.cdr.detectChanges();
  }

  private resetOwnerDocument(index: number) {
    this.owners.get(`${index}`).get('documents')?.patchValue({
      id: null,
      label: null,
    });

    const owner = this.pledge?.owners?.[index];
    if (owner) {
      owner.documents = null;
    }

    this.error = null;
    this.iframeUrls[index] = null;
    this.form.updateValueAndValidity();
  }

  ownerDocument(index: number) {
    return this.pledge?.owners?.[index]?.documents;
  }

  onGedDocumentSelected(doc: any, index: number, ownerId: number) {
    const documents = this.owners.get(`${index}`).get('documents');

    documents?.patchValue({
      id: doc?.id || null,
      label: doc?.label || null,
      document: doc?.document || documents?.get('document')?.value,
      dateExpired: doc?.dateExpired || documents?.get('dateExpired')?.value,
    });

    const owner = this.pledge?.owners?.[index];
    if (owner) {
      owner.documents = doc;
    }

    this.error = null;
    this.form.updateValueAndValidity();
    this.files = this.files.filter(file => file.ownerId !== ownerId);

    this.displayDocument(doc?.id, index).subscribe();

    if (this.certif && owner?.userId) {
      this.verifyIdentityDocument(owner.userId).subscribe();
    }

    this.cdr.detectChanges();
  }
  /**
   * Checks if file exists by comparing the length of two arrays of document and owner IDs.
   *
   * @param {Array<string>} ownerIds - Array of owner IDs.
   * @param {Array<string>} documentIds - Array of document IDs.
   * @return {boolean} Returns true if documentIds contains at least one element and ownerIds and documentIds have the same length
   */
  fileExists(ownerIds, documentIds) {
    const filteredIds = documentIds.filter(id => id);
    const ownerIdsLength = ownerIds?.length;
    const filteredIdsLength = filteredIds?.length;
    const idsAreEqual = ownerIdsLength === filteredIdsLength;
    return idsAreEqual && filteredIdsLength > 0;
  }
  private getOwnerDocumentsIds(): string[] {
    return this.form?.value?.owners?.map(doc => doc.documents.id) || [];
  }
  private shouldHandleNoFilesCase(documentsIdsFiltered): boolean {
    if (!this.files.length && !documentsIdsFiltered) {
      if (this.IDENTITY_DOCUMENT) {
        this.error = 'Veuillez ajouter un document';
      } else {
        this.subscriptionFacade.nextStep();
      }
      return true;
    }
    return false;
  }
  private shouldHandleNoFilesAndCertifiedSignatureCase(): boolean {
    if (
      !this.files?.length &&
      this.hasCertifiedSignature(this.subscriptionFacade.getPledge())
    ) {
      this.subscriptionFacade.nextStep();
      return true;
    }
    return false;
  }
  private getOwnerDocumentById(ownerId: number): any {
    return (
      this.form?.value?.owners?.find(doc => doc.ownerId === ownerId)
        ?.documents || {}
    );
  }
  private async checkFileRequirements(
    file: any,
    doc: any,
    documentsIdsFiltered: boolean,
  ): Promise<void> {
    const { document } = doc || {};

    // Une erreur a déjà été détectée, ou un document existe déjà côté serveur :
    // on ne re-valide pas.
    if (this.error || documentsIdsFiltered) {
      return;
    }
    if (!(document in documentRequirements)) {
      return;
    }

    const { minFiles } = documentRequirements[document];

    // Nombre EXACT de fichiers (CNI = 2 recto/verso, Passeport = 1) — aligné
    // sur le backend (GedValidationServiceImp).
    if (file.files.length !== minFiles) {
      this.error =
        document === 'PASSEPORT'
          ? 'Vous devez charger un seul fichier pour ce type de document'
          : 'Vous devez charger deux fichiers, un pour le recto et un pour le verso';
      return;
    }

    // Taille ≤ 4 Mo
    for (const f of file.files) {
      if (f && f.size > MAX_IDENTITY_FILE_SIZE) {
        this.error = 'La taille du fichier dépasse 4 Mo';
        return;
      }
    }

    // 1 page par PDF
    for (const f of file.files) {
      const pageCount = await countPdfPagesInFile(f);
      if (pageCount > 1) {
        this.error = 'Chaque fichier doit comporter une seule page';
        return;
      }
    }
  }

  private isValidForm(): boolean {
    return !this.error && this.form.valid;
  }
  /**
   * Executes the form submission process by selecting the error input and scrolling to it,
   * getting the pledge, ownerIds, documentsIds, and filtered documentsIds, checking if there
   * are no files and certified signatures, checking if the owner files are valid, and creating
   * the document form data if the form is valid.
   *
   * @return {void} This function does not return anything.
   */
  async onSubmit() {
    if (this.isRequestPending) {
      return;
    }

    this.error = null;
    this.subscriptionFacade.selectErrorInputAndScroll(this);
    const pledge = this.subscriptionFacade.getPledge();
    const ownerIds = pledge?.owners?.map(owner => owner.ownerId);
    const documentsIds = this.getOwnerDocumentsIds();
    const documentsIdsFiltered = this.fileExists(ownerIds, documentsIds);

    if (this.shouldHandleNoFilesCase(documentsIdsFiltered)) {
      return;
    }

    await this.checkOwnerFiles(ownerIds, documentsIdsFiltered);

    if (this.isValidForm()) {
      this.createDocumentFormData(pledge, documentsIdsFiltered);
    }
  }
  /**
   * Loops over an array of owner IDs and an array of files to check if there is a file for each owner.
   * If there is a file, it checks the file requirements against the owner's document and the filter flag.
   *
   * @param {number[]} ownerIds - An array of owner IDs to check files against.
   * @param {boolean} documentsIdsFiltered - A flag indicating whether documents are filtered.
   */
  private async checkOwnerFiles(
    ownerIds: number[],
    documentsIdsFiltered: boolean,
  ) {
    for (const ownerId of [...ownerIds]) {
      const doc = this.getOwnerDocumentById(ownerId);
      for (const file of this.files) {
        if (file.ownerId === ownerId) {
          await this.checkFileRequirements(file, doc, documentsIdsFiltered);
        }
      }
    }
  }

  private hasCertifiedSignature(pledge: PledgeRead) {
    return pledge?.products?.some(p => p?.parameters?.signatureCertified);
  }

  private createDocumentFormData(pledge: PledgeRead, documentsIdsFiltered) {
    let nested = [];
    pledge.owners.forEach(owner => {
      const doc =
        this.form?.value?.owners?.find(doc => doc.ownerId === owner.ownerId)
          ?.documents || {};
      const data = this.extractDocument({
        ...owner,
        ...doc,
      });

      if (data?.getRawValue()?.id) {
        nested.push(this.updateDocument(data.getRawValue()));
      } else {
        let formData = new FormData() as any;

        for (const file of this.files) {
          if (file.ownerId === owner.ownerId) {
            file.files.forEach(file => {
              formData.append('documentsFiles', file, file?.name);
            });
            const obj = data.getRawValue();
            const payloadDocument = this.formatPayloadForSubmitDocument(obj);

            formData.append(
              'document',
              new Blob([JSON.stringify(payloadDocument)], {
                type: 'application/json',
              }),
            );

            this.formData = formData;
          }
        }

        nested.push(this.createDocument(formData));
      }
    });
    this.uploadFiles(nested);
  }

  private formatPayloadForSubmitDocument(payload: any) {
    return {
      label: payload.label,
      dateCreated: payload.dateCreated,
      reviewed: payload.reviewed,
      isSigned: payload.isSigned,
      accountId: payload.accountId,
      category: payload.category,
      dateExpired: payload.dateExpired,
      id: payload.id,
      document: payload.document,
      ownerIds:
        payload.ownerIds && payload.ownerIds.length
          ? payload.ownerIds
          : payload.ownerIds
            ? [payload.ownerIds]
            : [],
    };
  }

  private uploadFiles(nested: any[]) {
    this.docLoading = true;
    this.isRequestPending = true;

    forkJoin(nested)
      .pipe(
        untilDestroyed(this),
        tap((resp: any) => {
          this.documentId = resp[0].id;
        }),

        catchError(err => {
          console.error(err);
          this.isRequestPending = false;
          this.docLoading = false;
          return err;
        }),
        delay(5000),
      )
      .subscribe(
        res => {
          this.resp = res;

          this.subscriptionFacade.submitPledgeWithoutForm(this);
          this.doneSubject.next(res);
          this.isRequestPending = false;
          this.docLoading = false;
        },
        error => {
          this.isRequestPending = false;
          this.docLoading = false;
          console.error(error);
          this.subscriptionFacade.nextStep();
        },
      );
  }

  updateDocument(rawValue?) {
    return this.documentDao
      .putDocument(
        rawValue,

        this.entity.accountId,
      )
      .pipe(take(1));
  }
  createDocument(formData) {
    return this.documentDao.saveDocument(formData, this.entity.accountId).pipe(
      take(1),
      catchError(err => {
        console.error(err);
        // Affiche le message de conformité précis renvoyé par le backend
        // (taille > 4 Mo, document multi-pages, nombre de fichiers incorrect).
        this.notificationService.error(
          err?.error?.message || err?.error?.informations?.errors,
        );
        return err;
      }),
    );
  }
  verfyProduct() {
    const products = this.subscriptionFacade.getPledge().products;
    const ids = products.map(product => product.productId + '');
    return this.productsDao.getProducts({ ids }).pipe(shareReplay(1));
  }

  /**
   * Extracts and merges data related to the pledge owners and their documents.
   *
   * @returns {Array<Object>} An array of objects with merged data for each pledge owner.
   *                          The merged data includes information about the owner and their
   *                          document, depending on the document type (CNI, Permis de conduire,
   *                          Titre de séjour, Passeport).
   */
  extract() {
    const pledge = this.subscriptionFacade.getPledge();

    const merged = pledge?.owners?.map(owner => {
      let doc;
      let cniNumber;
      // Determine whether the owner is the main subscriber or a co-subscriber
      const ownerId = !owner.physicalUser ? owner.userId : owner.ownerId;
      const isMain = owner.isMain;
      // Find the document related to the owner and extract its ID
      doc = this.resp.find(doc => doc.ownerIds.includes(ownerId));
      // If the owner is the main subscriber, extract their CNI number as well
      cniNumber = isMain
        ? this.form?.value?.owners?.find(doc => doc.ownerId === owner.ownerId)
            ?.documents?.cniNumber || undefined
        : undefined;
      // Extract the relevant data based on the document type
      const data = {
        CNI: { gedCniId: doc.id, cniNumber: cniNumber },
        // PERMIS_DE_CONDUIRE: { gedPermisDeConduireId: doc.id },
        // TITRE_DE_SEJOUR: { gedTitreDeSejourId: doc.id },
        PASSEPORT: { gedPassportId: doc.id },
      }[doc.document];

      const propertiesToCheckAndUndefine = [
        'gedCniId',
        // "gedPermisDeConduireId",
        // "gedTitreDeSejourId",
        'gedPassportId',
      ];

      // Set specified properties to undefined in owner
      const ownerWithUndefined = _.mapValues(owner, (value, key) => {
        return propertiesToCheckAndUndefine.includes(key) && !data?.[key]
          ? undefined
          : value;
      });

      // Merge the owner data and the document data and return it
      return {
        ...ownerWithUndefined,
        ...data,
      };
    });

    return merged;
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }
  getLabel(type) {
    return labelMap[type] || '';
  }

  displayDocument(docId, index): Observable<any> {
    this.displayDocumentLoading = true;

    const route = API_ROUTE_DOCUMENT.replace(
      ':accountId',
      this.entity?.accountId,
    ).replace(':documentId', docId);

    this.httpApiService.getWithOptions(route, {
      responseType: 'arraybuffer',
    });

    return this.httpApiService
      .getWithOptions(route, {
        responseType: 'arraybuffer',
      })
      .pipe(
        untilDestroyed(this),
        take(1),
        delay(1000),
        tap((documentContent: ArrayBuffer) => {
          const safeUrl = this.createIFrame(documentContent);
          this.iframeUrls[index] = safeUrl;
          this.displayDocumentLoading = false;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error(error);
          this.displayDocumentLoading = false;
          return throwError(error);
        }),
        finalize(() => {
          this.displayDocumentLoading = false;
        }),
      );
  }
  createIFrame(resp) {
    let blob = new Blob([resp], { type: 'application/pdf' });
    let dataUrl = window.URL.createObjectURL(blob);

    return this.sanitizer.bypassSecurityTrustResourceUrl(dataUrl);
  }
}
